import { buildUIState } from '@/agent-v2/utils/ui-state'
import { AgentV2UIStateContext } from '@/agent-v2/components/agent-v2-ui-state-context'
import { TableTitlesProvider } from '@/tables/components/TableTitlesProvider'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { getTableReference } from '../fetch/get-table-reference'
import { getTableChats } from '../fetch/get-table-chats'
import { postCreateTableChat } from '../fetch/post-create-table-chat'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackTableAssistantClosed } from '@/analytics/utils/table-analytics'
import CircularProgress from '@mui/material/CircularProgress'
import { useAppDispatch } from '@/store/store-hooks'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import { Button } from '@/components/button/Button'
import { useTableSidebar } from '../hooks/useTableSidebar'
import TableSourcePanel from './TableSourcePanel'
import clsx from 'clsx'
import { useIsSmallChatBreakpoint } from '@/hooks/useIsSmallChatBreakpoint'
import TableWorkspace from './TableWorkspace'
import TableChatHeader from './TableChatHeader'
import TableSourceTabs from './TableSourceTabs'
import TablePanelResizeHandle from './TablePanelResizeHandle'
import { ActiveTableReferenceContext, TableReferenceContext } from './TableReferenceContext'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import AgentV2ChatRoute from '@/routes/dashboard/AgentV2ChatRoute'
import { useChatTable, type TableNavigationState } from '@/tables/hooks/useChatTable'
import { matterPaths, kRouteSegments } from '@/routing/paths'
import { TableNavigationContext } from './TableNavigationContext'

/** Standalone tables share the chat layout's assistant/source sidebar. */
export default function StandaloneTableWorkspace(props: { matterId: string; tableId: string }) {
  const { trackEvent } = useAnalytics()
  const smallScreen = useIsSmallChatBreakpoint()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigationState = location.state as TableNavigationState | null
  const originatingConversationId = navigationState?.originatingConversationId
  const autoOpenAttempted = useRef(false)
  const tablePanelRef = useRef<HTMLElement>(null)
  const sidebarRef = useRef<HTMLElement>(null)
  const previousSidebarOpen = useRef(false)
  const previousTableFocus = useRef<HTMLElement | null>(null)
  const [params, setParams] = useSearchParams()
  const conversationId = params.get('chat')
  const referenceId = params.get('reference')
  const [referenceError, setReferenceError] = useState<string | null>(null)
  const [openingChat, setOpeningChat] = useState(false)
  const [chatOpenError, setChatOpenError] = useState<string | null>(null)
  const chatRequest = useRef<AbortController | null>(null)
  useEffect(
    () => () => {
      chatRequest.current?.abort()
      chatRequest.current = null
      autoOpenAttempted.current = false
    },
    []
  )
  const sidebar = useTableSidebar(true, Boolean(conversationId || referenceId || navigationState?.openAssistant), conversationId ?? undefined)
  const { openReference } = sidebar
  useEffect(() => {
    setReferenceError(null)
    if (!referenceId) return
    const controller = new AbortController()
    getTableReference(props.tableId, referenceId, controller.signal)
      .then((reference) => {
        if (!controller.signal.aborted && !openReference(reference)) {
          setReferenceError('This reference’s source is no longer available.')
        }
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setReferenceError('This saved reference could not be opened. It may have been removed or you may no longer have access.')
      })
    return () => controller.abort()
  }, [props.tableId, referenceId, openReference])
  const { refreshKey, openTable, linkError } = useChatTable(conversationId ?? '', props.tableId)
  const selectChat = (id: string, { clearOrigin = false, replace = false }: { clearOrigin?: boolean; replace?: boolean } = {}) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        next.set('chat', id)
        return next
      },
      { state: clearOrigin ? null : location.state, replace }
    )
  }
  const openChat = (id: string) => {
    setChatOpenError(null)
    selectChat(id)
    sidebar.openAssistant()
  }
  const openLatestChat = async (afterDeletion = false) => {
    sidebar.openAssistant()
    if (chatRequest.current || (conversationId && !afterDeletion)) return
    const controller = new AbortController()
    chatRequest.current = controller
    setOpeningChat(true)
    setChatOpenError(null)
    try {
      const chats = await getTableChats(props.tableId, controller.signal)
      if (controller.signal.aborted) return
      const existingChat = chats.find((chat) => chat.can_continue)
      const chat = existingChat ?? (await postCreateTableChat(props.tableId, controller.signal))
      if (controller.signal.aborted) return
      if (!existingChat) dispatch(AgentV2ConversationsActions.refreshConversationList({ matterId: props.matterId, forceRefresh: true }))
      selectChat(chat.id, {
        clearOrigin: afterDeletion && originatingConversationId === conversationId,
        replace: Boolean(navigationState?.openAssistant && !conversationId),
      })
    } catch (reason) {
      if (!controller.signal.aborted) setChatOpenError(reason instanceof Error ? reason.message : 'Unable to open the latest chat.')
    } finally {
      if (!controller.signal.aborted) setOpeningChat(false)
      if (chatRequest.current === controller) chatRequest.current = null
    }
  }
  useEffect(() => {
    if (!navigationState?.openAssistant || autoOpenAttempted.current) return
    autoOpenAttempted.current = true
    void openLatestChat()
  })
  const { open, width, setWidth, source, sourceVisible } = sidebar
  useLayoutEffect(() => {
    const panel = tablePanelRef.current
    if (!panel) return
    const wasOpen = previousSidebarOpen.current
    previousSidebarOpen.current = open
    if (open && !wasOpen) {
      const active = document.activeElement
      previousTableFocus.current = active instanceof HTMLElement && panel.contains(active) ? active : null
    }
    panel.inert = open && smallScreen
    if (open && smallScreen) sidebarRef.current?.focus()
    if (!open && wasOpen) {
      const previous = previousTableFocus.current
      if (previous?.isConnected && panel.contains(previous)) previous.focus()
      if (!previous || document.activeElement !== previous) panel.focus()
    }
    return () => {
      panel.inert = false
    }
  }, [open, smallScreen])
  const uiState = useMemo(() => buildUIState({ sourceSession: source, tableId: props.tableId, artifactId: null }), [source, props.tableId])
  return (
    <TableTitlesProvider key={props.matterId} refreshKey={refreshKey}>
      <div className={clsx('relative flex min-h-0 w-full flex-1 overflow-hidden', open && !smallScreen && 'py-4 pl-2 pr-4')}>
        <section
          ref={tablePanelRef}
          tabIndex={-1}
          aria-hidden={open && smallScreen ? true : undefined}
          aria-label="Table panel"
          className={clsx(
            'min-w-0',
            open && smallScreen ? 'overflow-y-auto' : 'flex min-h-0 flex-col overflow-hidden',
            open && !smallScreen && 'rounded-lg border border-brand-neutral-300 bg-white'
          )}
          style={{ width: open && !smallScreen ? `${width}%` : '100%' }}
        >
          <ActiveTableReferenceContext.Provider value={sidebar.activeReferenceId}>
            <TableReferenceContext.Provider value={sidebar.openReference}>
              <TableWorkspace
                {...props}
                refreshKey={refreshKey}
                chatOpen={open && !smallScreen}
                onOpenAssistant={open ? undefined : () => void openLatestChat()}
                onBackToChat={
                  originatingConversationId
                    ? () => navigate(matterPaths.chat.conversation(props.matterId, kRouteSegments.agentV2, originatingConversationId))
                    : undefined
                }
              />
            </TableReferenceContext.Provider>
          </ActiveTableReferenceContext.Provider>
        </section>
        {open && !smallScreen && <TablePanelResizeHandle width={width} onResize={setWidth} />}
        <aside
          hidden={!open}
          ref={sidebarRef}
          tabIndex={-1}
          aria-label="Table assistant and sources"
          className={clsx(
            'min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-brand-neutral-300 bg-white',
            open ? 'flex' : 'hidden',
            smallScreen && 'absolute inset-0 z-20'
          )}
        >
          {referenceError && (
            <p role="alert" className="p-4 text-sm text-red-800">
              {referenceError}
            </p>
          )}
          <TableSourceTabs
            title={source?.document.title}
            sourceVisible={sourceVisible}
            onSelect={(source) => {
              if (!source && !conversationId) void openLatestChat()
              else sidebar.selectSource(source)
            }}
            onClose={sidebar.closeSource}
            // Let a pending creation finish without reopening the panel or starting another request.
            onCollapse={() => {
              trackTableAssistantClosed(trackEvent, props.tableId)
              sidebar.close()
            }}
          />
          <div className="relative flex min-h-0 flex-1 flex-col">
            <div
              role="tabpanel"
              id="table-chat-content"
              aria-labelledby="table-chat-tab"
              className={clsx('flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-brand-neutral-gradient', sourceVisible && 'hidden')}
            >
              {chatOpenError && (
                <div role="alert" className="px-4 py-2 text-sm text-red-800">
                  {chatOpenError}{' '}
                  <Button variant="plain" size="sm" onClick={() => void openLatestChat()}>
                    Retry
                  </Button>
                </div>
              )}
              <TableChatHeader
                tableId={props.tableId}
                matterId={props.matterId}
                conversationId={conversationId}
                disabled={openingChat}
                onOpenChat={openChat}
                onDeleted={() => {
                  setParams(
                    (current) => {
                      const next = new URLSearchParams(current)
                      next.delete('chat')
                      return next
                    },
                    { state: originatingConversationId === conversationId ? null : location.state }
                  )
                  void openLatestChat(true)
                }}
              />
              {openingChat ? (
                <div className="flex h-full w-full items-center justify-center">
                  <CircularProgress size={24} thickness={5} aria-label="Loading chat" />
                </div>
              ) : conversationId ? (
                <TableNavigationContext.Provider value={{ matterId: props.matterId, openTable }}>
                  {linkError && (
                    <p role="alert" className="px-4 py-2 text-sm text-red-800">
                      {linkError}
                    </p>
                  )}
                  <AgentV2UIStateContext.Provider value={uiState}>
                    <AgentV2ChatRoute
                      embeddedConversationId={conversationId}
                      onOpenExternalArtifact={(versionId) =>
                        navigate(`${matterPaths.chat.conversation(props.matterId, kRouteSegments.agentV2, conversationId)}?draft=${versionId}`)
                      }
                    />
                  </AgentV2UIStateContext.Provider>
                </TableNavigationContext.Provider>
              ) : null}
            </div>
            {source && <TableSourcePanel visible={sourceVisible} conversationId={conversationId ?? undefined} />}
          </div>
        </aside>
      </div>
    </TableTitlesProvider>
  )
}
